import {
  useApiClient,
  useReviewGetById,
  useReviewInactivate,
  type WebshopReviewDetail,
  WebshopReviewQueries,
  webshopReviewGetReviewsService,
} from '@cldsystems/ws-api-client';
import { faPen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@heroui/react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon } from '@ui';
import { FC, useMemo, useState } from 'react';
import { toast } from 'sonner';
import NoNata from '@/components/common/no-data/no-data';
import useProductAverageRating from '@/hooks/use-product-average-rating';
import useMyProductReview from '@/hooks/use-product-reviews';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import RatingStars from '../../rating-stars/rating-stars';
import CardReview from '../card-review/card-review';
import ModalReview from '../modal-review/modal-review';
import RemovedReviewBanner from '../removed-review-banner/removed-review-banner';
import { ReviewType } from '../review/review';

type Props = {
  /**
   * Sibling product ids for one SKU — the locale being viewed first. Reviews are
   * filed against a single locale's id, so they are read across all of them.
   */
  productIds: string[];
};

const PAGE_SIZE = 10;

/** How far into each locale's review list the next page should read. */
type ReviewCursors = Record<string, number>;

type ReviewLike = {
  user: { fullName: string };
  ranking: number;
  title: string;
  message: string;
  images: { url: string; id: string }[];
};

const toReviewType = (review: ReviewLike): ReviewType => ({
  user: {
    name: review.user.fullName,
    avatar: '',
  },
  rating: review.ranking,
  title: review.title,
  content: review.message,
  images: review.images,
});

const ReviewsTab: FC<Props> = ({ productIds }) => {
  const { t, tFormat } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { average: avgRating, count: reviewCount } = useProductAverageRating(productIds);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { isAuthenticated } = useKeycloak();
  const queryClient = useQueryClient();
  const reviewInactivate = useReviewInactivate();
  const { httpClient, hasSchema } = useApiClient();

  // The current locale's product is the one a new review gets filed against.
  const currentProductId = productIds[0];
  const initialCursors = useMemo(
    () => Object.fromEntries(productIds.map(id => [id, 0])) as ReviewCursors,
    [productIds],
  );

  const {
    data: reviewsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isFetched,
  } = useInfiniteQuery({
    queryKey: [WebshopReviewQueries.GetReviews, productIds, 'infinite'],
    initialPageParam: initialCursors,
    enabled: Boolean(httpClient) && !isStudioPreview,
    // Each locale keeps its own cursor: one shared offset would re-read rows from
    // whichever lists ran out first.
    queryFn: async ({ pageParam }) => {
      const entries = Object.entries(pageParam);
      const pages = await Promise.all(
        entries.map(([productId, from]) =>
          webshopReviewGetReviewsService({
            queries: { productId, from: String(from), size: String(PAGE_SIZE) },
            httpClient,
            hasSchema: Boolean(hasSchema),
          })(),
        ),
      );

      const reviews: WebshopReviewDetail[] = [];
      const nextCursors: ReviewCursors = {};

      pages.forEach((page, index) => {
        const [productId, from] = entries[index];
        reviews.push(...(page ?? []));
        // A short page means that locale is exhausted; drop it from the cursors so
        // later pages stop asking for it.
        if (page && page.length === PAGE_SIZE) nextCursors[productId] = from + PAGE_SIZE;
      });

      return { reviews, nextCursors };
    },
    getNextPageParam: lastPage =>
      Object.keys(lastPage.nextCursors).length > 0 ? lastPage.nextCursors : undefined,
  });

  const { myReview, userId } = useMyProductReview(productIds);
  const myReviewId = myReview?.reviewId;

  const items = useMemo(() => {
    const all = (reviewsPages?.pages ?? []).flatMap(page => page.reviews);
    if (!userId) return all;
    // Removes the user's own review from every locale, not just this one.
    return all.filter(review => review.user.id !== userId);
  }, [reviewsPages?.pages, userId]);

  const { data: userReviewVersioned } = useReviewGetById({
    params: { id: myReviewId ?? '' },
    enabled:
      !!myReviewId &&
      !isStudioPreview &&
      (isOpen || isDeleteConfirmOpen || !!myReview?.removalReason),
  });

  const orderedReviews: ReviewType[] = useMemo(() => {
    const list = items.map(toReviewType);
    if (!myReview) return list;
    return [toReviewType(myReview), ...list];
  }, [items, myReview]);

  const handleDelete = () => {
    if (!myReview || !userReviewVersioned) return;
    const deletedId = myReview.reviewId;
    reviewInactivate.mutate(
      {
        params: {
          id: deletedId,
          version: String(userReviewVersioned.version),
        },
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetReviews] });
            queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetMyReviews] });
            queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetById, deletedId] });
          }, 1000);
          toast.success(t?.productDetail.addReviewModal.successDelete);
          setIsDeleteConfirmOpen(false);
        },
      },
    );
  };

  const editTarget =
    isEditing && myReview && userReviewVersioned
      ? {
          id: myReview.reviewId,
          version: userReviewVersioned.version,
          // The locale the review was filed under, which is not necessarily the one
          // being viewed — updating with the viewed locale's id would move it.
          productId: myReview.productId,
          initialValues: {
            rating: myReview.ranking,
            title: myReview.title,
            message: myReview.message,
            images: myReview.images ?? [],
          },
        }
      : undefined;

  return (
    <section className="w-full">
      <header className="mb-8 flex flex-wrap justify-between gap-4 md:px-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-medium md:text-large font-semibold">{t?.productDetail.reviews}</h1>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <RatingStars rating={avgRating} starClassName="text-base sm:text-xl" />
            <span className="text-medium md:text-large font-semibold">{avgRating.toFixed(1)}</span>
            <span className="text-small text-default-600 lg:text-medium">
              {reviewCount === 0
                ? t?.productDetail.noReviewsYet
                : tFormat(t?.productDetail.basedOnReview, { reviewsLength: reviewCount })}
            </span>
          </div>
        </div>
        {isAuthenticated && !myReview && (
          <Button
            endContent={<Icon icon={faPen} />}
            variant="bordered"
            onClick={() => {
              setIsEditing(false);
              setIsOpen(true);
            }}
          >
            {t?.productDetail.writeAReview}
          </Button>
        )}
        {!isAuthenticated && (
          <span className="text-default-600 text-small self-center">
            {t?.productDetail.loginToReview}
          </span>
        )}
      </header>
      {isPending && !isFetched ? (
        <div className="flex justify-center py-12">
          <Spinner label={t?.productDetail.reviews} />
        </div>
      ) : orderedReviews.length === 0 ? (
        <NoNata label={t?.productDetail.noReviewsYet ?? ''} className="my-52 text-black" />
      ) : (
        <div className="flex flex-col gap-4">
          {orderedReviews.map((review, index) => {
            const isOwn = index === 0 && !!myReview;
            const key = `${index}-${review.title}`;

            if (!isOwn) {
              return <CardReview key={key} {...review} />;
            }

            const removalReason = myReview?.removalReason;
            const isRemovedByAdmin = !!removalReason;

            if (isRemovedByAdmin) {
              return (
                <RemovedReviewBanner
                  key={key}
                  onCreateNew={() => {
                    setIsEditing(false);
                    setIsOpen(true);
                  }}
                  removalReason={removalReason!}
                  removedAtRaw={userReviewVersioned?.review.updateAudit?.date}
                  review={review}
                />
              );
            }

            return (
              <div key={key} className="flex flex-col">
                <div className="bg-default-500 text-tiny rounded-t-medium w-fit px-5 py-2 font-semibold text-white">
                  {t?.productDetail.addReviewModal.yourReview}
                </div>
                <CardReview
                  {...review}
                  className="border-default-300 bg-default-100 rounded-tl-none border-l-4 shadow-md"
                >
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      className="text-default-600 hover:text-default-900 text-small inline-flex items-center gap-1.5 transition-colors"
                      type="button"
                      onClick={() => {
                        setIsEditing(true);
                        setIsOpen(true);
                      }}
                    >
                      <Icon icon={faPen} />
                      {t?.productDetail.addReviewModal.editReview}
                    </button>
                    <button
                      className="text-danger/80 hover:text-danger text-small inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      disabled={reviewInactivate.isPending || !myReviewId}
                      type="button"
                      onClick={() => setIsDeleteConfirmOpen(true)}
                    >
                      <Icon icon={faTrash} />
                      {t?.productDetail.addReviewModal.deleteReview}
                    </button>
                  </div>
                </CardReview>
              </div>
            );
          })}
          <div className="flex justify-center py-2">
            {hasNextPage && (
              <Button
                isLoading={isFetchingNextPage}
                variant="bordered"
                onClick={() => fetchNextPage()}
              >
                {t?.productDetail.loadMoreReviews}
              </Button>
            )}
          </div>
        </div>
      )}
      <ModalReview
        editTarget={editTarget}
        isOpen={isOpen}
        productId={currentProductId}
        onOpenChange={open => {
          setIsOpen(open);
          if (!open) setIsEditing(false);
        }}
      />
      <Modal isOpen={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <ModalContent>
          <ModalHeader>{t?.productDetail.addReviewModal.deleteConfirmTitle}</ModalHeader>
          <ModalBody>
            <p className="text-default-700">
              {t?.productDetail.addReviewModal.deleteConfirmMessage}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={reviewInactivate.isPending}
              variant="light"
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              {t?.productDetail.addReviewModal.deleteConfirmCancel}
            </Button>
            <Button color="danger" isLoading={reviewInactivate.isPending} onClick={handleDelete}>
              {t?.productDetail.addReviewModal.deleteConfirmConfirm}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  );
};

export default ReviewsTab;
